import { AUTH_ENDPOINTS, STORAGE_KEYS } from './authConstants';
import { getToken, setToken, removeToken } from '../../utils/tokenHelper';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Helper to handle API responses
const handleResponse = async (response) => {
  // Handle empty responses (204 No Content)
  if (response.status === 204) {
    return { status: true, message: 'Success' };
  }

  let data;
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    const text = await response.text();
    data = text ? { message: text } : { status: true };
  }

  if (!response.ok) {
    const errorMessage = data?.message || data?.Message || data?.error || `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // Support 'Data', 'data', 'Response' or 'response' keys for the main payload
  const normalizedData = data?.Data !== undefined ? data.Data : 
                        (data?.data !== undefined ? data.data : 
                        (data?.Response !== undefined ? data.Response : 
                        (data?.response !== undefined ? data.response : data)));
  const normalizedStatus = data?.Status ?? data?.status ?? true;
  const normalizedMessage = data?.Message || data?.message || 'Success';

  return {
    status: normalizedStatus,
    data: normalizedData,
    message: normalizedMessage,
    // Store original structure for backup
    _original: data
  };
};

// App Key from environment
const APP_KEY = import.meta.env.VITE_APP_KEY || '';

// Helper to get headers with auth token
const getAuthHeaders = () => {
  const token = getToken();
  const appId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';
  const memberId = localStorage.getItem(STORAGE_KEYS.MEMBER_ID) || '';
  
  const headers = {
    'Content-Type': 'application/json',
    'appkey': APP_KEY,
    'appId': appId,
    'memberId': memberId,
    ...(token && { Authorization: `Bearer ${token}` }),
  };
  return headers;
};

// Generic fetch wrapper with token refresh
export const fetchWithAuth = async (url, options = {}) => {
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  if (isFormData) {
    delete headers['Content-Type'];
  }

  try {
    let response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });

    // Handle 401 - try refresh token
    if (response.status === 401) {
      const refreshed = await refreshTokenService();

      if (refreshed) {
        // Retry original request with new token
        headers.Authorization = `Bearer ${getToken()}`;
        response = await fetch(`${API_BASE_URL}${url}`, {
          ...options,
          headers,
        });
      } else {
        // Refresh failed - logout
        removeToken();
        throw new Error('Session expired. Please login again.');
      }
    }

    return handleResponse(response);
  } catch (error) {
    if (error.message === 'Failed to fetch') {
      throw new Error('Network error. Please check your connection.');
    }
    throw error;
  }
};

// Refresh token service
const refreshTokenService = async () => {
  try {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const memberId = localStorage.getItem(STORAGE_KEYS.MEMBER_ID);
    const token = getToken();

    if (!refreshToken || !memberId) {
      console.log('[Auth] No refresh token or memberId found');
      return false;
    }

    console.log('[Auth] Attempting to refresh token...');

    const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REFRESH_TOKEN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'appkey': APP_KEY,
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        memberId: Number(memberId),
        refreshToken,
      }),
    });

    // Handle non-OK responses
    if (!response.ok) {
      console.log('[Auth] Refresh token request failed:', response.status);
      if (response.status === 401) {
        // Refresh token is invalid/expired
        removeToken();
        // Redirect if needed or handle in caller
      }
      return false;
    }

    const data = await handleResponse(response);
    const responseData = data?.data || data;

    console.log('[Auth] Refresh token response normalized:', { hasAccessToken: !!responseData?.authToken });

    const accessToken = responseData?.authToken || responseData?.accessToken;
    const newRefreshToken = responseData?.refreshToken || refreshToken;
    const newMemberId = responseData?.memberId || memberId;

    if (accessToken) {
      setToken(accessToken, newRefreshToken, newMemberId);
      console.log('[Auth] Token refreshed successfully');
      return true;
    }

    console.log('[Auth] No access token in refresh response');
    return false;
  } catch (error) {
    console.error('[Auth] Refresh token error:', error);
    return false;
  }
};

// Helper for public API calls (no auth token needed)
const getPublicHeaders = () => ({
  'Content-Type': 'application/json',
  'appkey': APP_KEY, // Changed from appId to appkey
});

// Login
export const loginService = async (credentials) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGIN}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(credentials),
  });

  return handleResponse(response);
};

// Verify OTP
export const verifyOtpService = async (data) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.VERIFY_OTP}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

// Register
export const registerService = async (data) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REGISTER}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

// Get Profile - with exhaustive fallback mapping
export const getProfileService = async () => {
  const response = await fetchWithAuth(AUTH_ENDPOINTS.GET_PROFILE, {
    method: 'GET',
  });

  // Extract raw record
  // handleResponse already extracts data.Data into response.data
  // but we also check _original.Data for safety
  const rawData = response?.data || response?._original?.Data || (response?.data?.Data) || response || {};

  // If status is false, or rawData is essentially empty (no name/email/id), throw error to avoid clearing state with defaults
  const hasBasicInfo = rawData.Name || rawData.name || rawData.Email || rawData.email || rawData.MemberId || rawData.memberId || rawData.memberid;

  if (response?.status === false || !hasBasicInfo) {
    const errorMsg = response?.message || 'Profile data missing or invalid';
    throw new Error(errorMsg);
  }

  const defaultProfile = {
    memberId: null,
    name: '',
    email: '',
    businessName: '',
    businessCategory: '',
    mobileNumber: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    gstIn: '',
    website: '',
    logo: '',
    isActive: true,
    lastLoginDate: null,
    createdDate: null,
  };

  // Map raw data with exhaustive case-insensitive logic
  const normalizedProfile = {
    ...defaultProfile,
    memberId: rawData.memberId || rawData.MemberId || rawData.Memberid || rawData.id || defaultProfile.memberId,
    name: rawData.name || rawData.Name || rawData.fullName || rawData.FullName || rawData.firstName || rawData.username || defaultProfile.name,
    email: rawData.email || rawData.Email || rawData.emailId || rawData.EmailId || defaultProfile.email,
    businessName: rawData.businessName || rawData.BusinessName || rawData.businessname || rawData.company || rawData.Company || defaultProfile.businessName,
    businessCategory: rawData.businessCategory || rawData.BusinessCategory || defaultProfile.businessCategory,
    mobileNumber: rawData.mobileNumber || rawData.MobileNumber || rawData.mobilenumber || rawData.mobile || rawData.Mobile || rawData.phone || defaultProfile.mobileNumber,
    address: rawData.address || rawData.Address || defaultProfile.address,
    city: rawData.city || rawData.City || defaultProfile.city,
    state: rawData.state || rawData.State || defaultProfile.state,
    pincode: rawData.pincode || rawData.Pincode || rawData.zipCode || defaultProfile.pincode,
    gstIn: rawData.gstIn || rawData.GSTIn || rawData.GstIn || rawData.gstin || rawData.GSTIN || defaultProfile.gstIn,
    website: rawData.website || rawData.Website || defaultProfile.website,
    logo: rawData.logo || rawData.Logo || defaultProfile.logo,
    isActive: rawData.isActive ?? rawData.IsActive ?? defaultProfile.isActive,
    lastLoginDate: rawData.lastLoginDate || rawData.LastLoginDate || defaultProfile.lastLoginDate,
    createdDate: rawData.createdDate || rawData.CreatedDate || rawData.createDate || defaultProfile.createdDate,
  };

  return normalizedProfile;
};

// Update Profile - with support for FormData (file uploads)
export const updateProfileService = async (profileData) => {
  const isFormData = profileData instanceof FormData;
  
  const response = await fetchWithAuth(AUTH_ENDPOINTS.UPDATE_PROFILE, {
    method: 'POST',
    // Let the browser set Content-Type for FormData to include the boundary
    headers: isFormData ? {} : { 'Content-Type': 'application/json' },
    body: isFormData ? profileData : JSON.stringify(profileData),
  });

  // If response doesn't contain data, merge submitted data with success status
  // Note: For FormData, we can't easily spread it into a new object here
  if (!response?.data && response?.status) {
    return {
      ...response,
      data: isFormData ? {} : profileData,
    };
  }

  return response;
};

// Social Login
export const socialLoginService = async (data) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.SOCIAL_LOGIN}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

// Request Forgot Password OTP
export const forgotPasswordOtpService = async (email) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.REQUEST_FORGOT_PASSWORD_OTP}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(email),
  });

  return handleResponse(response);
};

// Forgot Password (Public API)
export const forgotPasswordService = async (data) => {
  const response = await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.FORGOT_PASSWORD}`, {
    method: 'POST',
    headers: getPublicHeaders(),
    body: JSON.stringify(data),
  });

  return handleResponse(response);
};

// Change Password (Requires Authentication)
export const changePasswordService = async (data) => {
  return fetchWithAuth(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
};

// Logout
export const logoutService = async () => {
  try {
    // Try to call logout API first (best effort)
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_BASE_URL}${AUTH_ENDPOINTS.LOGOUT}`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
      } catch (apiError) {
        // API call failed but we still want to clear local tokens
        console.log('[Auth] Logout API call failed, clearing local tokens anyway');
      }
    }
  } finally {
    // Always clear local tokens regardless of API success
    removeToken();
  }
  return { success: true };
};
